import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { subscribeToDiscipline, subscribeToEventBouts } from "../firebase/services";
import SpectateBoutView, { SpectateLineupView } from "../components/SpectateBoutView";
import UnavailableView from "../components/UnavailableView";

export default function SpectatePool() {
  const { eventId, disciplineId, poolName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disc, setDisc] = useState(null);
  const [bouts, setBouts] = useState([]);

  useEffect(() => {
    const unsub = subscribeToDiscipline(eventId, disciplineId, setDisc);
    return unsub;
  }, [eventId, disciplineId]);

  const decodedPoolName = decodeURIComponent(poolName);

  useEffect(() => {
    const unsub = subscribeToEventBouts(eventId, (allBouts) => {
      setBouts(allBouts.filter(b => b.disciplineId === disciplineId && b.poolName === decodedPoolName)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    });
    return unsub;
  }, [eventId, disciplineId, decodedPoolName]);

  const pool = disc?.pools?.find((p) => p.name === decodedPoolName);
  const activeIdx = bouts.findIndex((b) => b.id === pool?.activeBoutId);
  const nextBout = activeIdx >= 0 ? bouts[activeIdx + 1] : undefined;
  const onDeckBout = activeIdx >= 0 ? bouts[activeIdx + 2] : undefined;

  if (!disc) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-gray-600 font-display text-xl" style={{ zIndex: 9999 }}>
        Loading...
      </div>
    );
  }
  if (!pool) {
    return <UnavailableView eventId={eventId} title="Pool No Longer Available"
      message="This pool was removed or the tournament was reset. Return to the event page for the current schedule." />;
  }

  // A shared pool link always opens its schedule first, including before the
  // referee has selected a live bout. This keeps the QR destination useful
  // throughout the event rather than showing a dead-end waiting screen.
  if (!pool.activeBoutId) {
    return <SpectateLineupView eventId={eventId} disciplineName={disc.name} poolName={pool.name} bouts={bouts} currentBoutId={null}
      onClose={() => navigate(`/public/${eventId}`)} />;
  }

  if (false) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-black p-6 text-center" style={{ zIndex: 9999 }}>
        <div className="text-4xl mb-4">⏳</div>
        <h2 className="font-display text-lg font-bold text-white mb-2">Waiting for Ref</h2>
        <p className="text-gray-500 text-sm mb-6">{pool.name}</p>
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-yellow-500 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>
        <button onClick={() => navigate("/")}
          className="absolute bottom-3 left-3 bg-black bg-opacity-50 text-gray-500 text-xs px-3 py-1.5 rounded-lg border-none cursor-pointer hover:text-gray-300 font-display">
          ← EXIT
        </button>
      </div>
    );
  }

  return (
    <SpectateBoutView
      key={pool.activeBoutId}
      eventId={eventId}
      boutId={pool.activeBoutId}
      onExit={() => navigate("/")}
      nextBout={nextBout}
      onDeckBout={onDeckBout}
      spectatorCode={pool.spectateCode}
      disciplineName={disc.name}
      poolName={pool.name}
      poolBouts={bouts}
      defaultLineup={searchParams.get("view") !== "live"}
      onViewChange={(view) => setSearchParams({ view }, { replace: true })}
    />
  );
}
